/*
 * Opens the Supplement Facts drawer from the PDP trigger link.
 *
 * The theme wires [data-js-sidebar-handle] inside the MainHeader constructor
 * (section-header.js), which runs before the product section is parsed, so PDP
 * triggers are never picked up there. Delegating on document instead also keeps
 * the trigger working after a theme editor section re-render.
 *
 * Closing is already handled by SidebarDrawer (close button, ESC) and the
 * header's .site-overlay click handler, both of which reset aria-expanded.
 */

/*
 * The theme's other sidebar-drawers (site-cart-sidebar, site-search-sidebar,
 * site-menu-sidebar) are all rendered as direct children of <body> in
 * layout/theme.liquid, alongside .site-overlay. Ours is rendered from inside
 * sections/main-product.liquid, which puts it inside <main>. Even with no
 * transform/filter/etc. on any ancestor, that nesting breaks the
 * stacking-context comparison against .site-overlay: the overlay wins
 * hit-testing over the drawer, so wheel-scroll and clicks inside the drawer's
 * content fall through to the overlay/page instead of landing on the drawer.
 *
 * component-modal.js already has an established fix for exactly this class of
 * problem (its `appendToBody` option does the same document.body.appendChild
 * for <modal-box> elements) — mirroring that here.
 */
const supplementFactsDrawerEl = document.getElementById("supplement-facts-sidebar");
if (supplementFactsDrawerEl && supplementFactsDrawerEl.parentElement !== document.body) {
  document.body.appendChild(supplementFactsDrawerEl);
}

/*
 * Fixes up each key-ingredient link to the same /pages/ingredient/{handle}
 * convention used by ingredient-card.liquid (see getIngredientPageHandle in
 * ingredient-handle.js, shared by every place that builds this link). The
 * href set server-side is a fallback this corrects on load. There can be
 * more than one now that key-ingredients-card.liquid renders a list.
 */
document.querySelectorAll("[data-ingredient-link]").forEach((link) => {
  const finalHandle = getIngredientPageHandle(link.dataset.ingredientHandle);
  link.href = `/pages/ingredient/${finalHandle}`;
});

/*
 * Focus management for the Supplement Facts drawer.
 *
 * <sidebar-drawer> (section-header.js) already locks body scroll and closes
 * on ESC/overlay-click/close-button, but it never moves focus into the
 * drawer on open, and the background-inert list it relies on
 * (window.inertElems, built from [data-js-inert]) is a curated set of page
 * containers that doesn't cover the PDP's own content — so without this,
 * Tab walks focus straight out of the drawer and into the page behind it.
 *
 * This lives here (rather than in the shared SidebarDrawer class) to keep
 * the fix scoped to this one drawer instance, same as the appendToBody
 * patch above — the cart/search/menu drawers are unaffected.
 */
let supplementFactsLastTrigger = null;
let supplementFactsOpen = false;

function getSupplementFactsFocusable() {
  if (!supplementFactsDrawerEl) return [];
  return Array.from(
    supplementFactsDrawerEl.querySelectorAll(
      'a[href], button:not([disabled]), input:not([disabled]), select:not([disabled]), textarea:not([disabled]), [tabindex]:not([tabindex="-1"])'
    )
  ).filter((el) => el.offsetParent !== null);
}

if (supplementFactsDrawerEl) {
  // SidebarDrawer.hide() removes "sidebar--opened" synchronously as its
  // first step, so watching that class is a reliable, drawer-scoped signal
  // that this drawer just closed — however the close happened (close
  // button, ESC, or overlay click, all handled by SidebarDrawer itself).
  new MutationObserver(() => {
    if (!supplementFactsOpen) return;
    if (supplementFactsDrawerEl.classList.contains("sidebar--opened")) return;

    supplementFactsOpen = false;
    if (supplementFactsLastTrigger && document.contains(supplementFactsLastTrigger)) {
      supplementFactsLastTrigger.focus();
    }
    supplementFactsLastTrigger = null;
  }).observe(supplementFactsDrawerEl, { attributes: true, attributeFilter: ["class"] });

  document.addEventListener("keydown", (e) => {
    if (!supplementFactsOpen || e.key !== "Tab") return;

    const focusable = getSupplementFactsFocusable();
    if (focusable.length === 0) {
      e.preventDefault();
      supplementFactsDrawerEl.focus();
      return;
    }

    const first = focusable[0];
    const last = focusable[focusable.length - 1];

    if (e.shiftKey && document.activeElement === first) {
      e.preventDefault();
      last.focus();
    } else if (!e.shiftKey && document.activeElement === last) {
      e.preventDefault();
      first.focus();
    } else if (!supplementFactsDrawerEl.contains(document.activeElement)) {
      // Focus somehow landed outside the drawer (e.g. programmatic focus
      // elsewhere) — pull it back in rather than letting Tab continue out.
      e.preventDefault();
      first.focus();
    }
  });
}

document.addEventListener("click", (e) => {
  const handle = e.target.closest("[data-js-supplement-facts-handle]");
  if (!handle) return;

  e.preventDefault();

  const drawer = document.getElementById(handle.getAttribute("aria-controls"));
  if (!drawer || typeof drawer.show !== "function") return;

  handle.setAttribute("aria-expanded", "true");
  supplementFactsLastTrigger = handle;
  supplementFactsOpen = true;
  drawer.show();

  /*
   * show() sets display:grid synchronously (the sidebar--opened class that
   * drives the slide-in transition is only added 15ms later), so the drawer
   * is already focusable here. It carries tabindex="-1" + role="dialog" +
   * aria-labelledby for exactly this.
   */
  drawer.focus();
});
